/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import getInvoicePdf from "@eisinc/App/Handlers/GetInvoicePdf";
import { iconsSharedStyles } from "@eisinc/App/SharedStyles/IconsStyles";
import { infoDisplayStylesV2, InfoDisplayStylesV2 } from "@eisinc/App/SharedStyles/InfoDisplayStylesV2";
import { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import Modal from "@insite/mobius/Modal";
import { OrderDetailsPageContext } from "@eisinc/Overrides/Pages/OrderDetailsPage";
import Pdf from "@eisinc/XDL/Icons/Pdf";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Clickable from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Link from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface OrderDetailsInvoicesNumbersStyles {
    iconStyle?: IconPresentationProps;
    pdfButton?: ButtonPresentationProps;
    linksWrapper?: InjectableCss;
    wrapper?: InjectableCss;
    textStyle?: InjectableCss;
    modalPDFWrapper?: InjectableCss;
}

interface InvoicesParam {
    invoicePdf: string;
}

export const orderDetailsInvoicesNumbersStyles: OrderDetailsInvoicesNumbersStyles = {
    iconStyle: {
        size: 18,
        color: "primary.main",
        css: css`
            display: inline;
            margin-left: 5px;
        `,
    },
    pdfButton: {
        css: css`
            ${iconsSharedStyles}
        `,
    },
    linksWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: row;
        `,
    },
    textStyle: {
        css: css`
            text-overflow: ellipsis;
            width: 90px;
        `,
    }
};

const styles: InfoDisplayStylesV2 = infoDisplayStylesV2;

const mapDispatchToProps = {
    getInvoicePdf,
};

type Props = ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const OrderDetailsInvoicesNumbers = (props: Props) => {
    const { value: order } = useContext(OrderStateContext);
    const toasterContext = React.useContext(ToasterContext);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [invoices, setInvoices] = React.useState([""]);
    const [selectedInvoiceNumber, setSelectedInvoiceNumber] = React.useState<string | null>(null);

    if (!order || !order.webOrderNumber) {
        return null;
    }

    const modalCloseHandler = () => {
        setIsModalOpen(false);
    };

    const handlePDFClick = (invoiceNumber: string) => {
        setIsLoading(true);
        props.getInvoicePdf({
            invoiceNumber,
            onSuccess: (response: any) => {
                setInvoices(response.invoices);
                setIsLoading(false);

                if (response.invoices.length > 1) {
                    setSelectedInvoiceNumber(invoiceNumber);
                    setIsModalOpen(true);
                } else {
                    handlePDFDownload(invoiceNumber, response?.invoices?.[0]);
                }
            },
            onError: (error: any) => {
                toasterContext.addToast({ body: translate("No Available PDF file"), messageType: "danger" });
                setIsLoading(false);
            },
        });
    };

    const handlePDFDownload = (invoiceNumber: string, invoice?: InvoicesParam) => {
        const pdfData = invoice?.invoicePdf;

        if(!pdfData) {
            return;
        }
        const fileName = `invoice ${invoiceNumber}.pdf`;
        const dataUri = `data:application/pdf;base64,${pdfData}`;
        const link = document.createElement("a");
        link.href = dataUri;
        link.target = "_blank"; 
        link.download = fileName;

        document.body.appendChild(link);

        const clickEvent = new MouseEvent("click", {
            view: window,
            bubbles: true,
            cancelable: false,
        });
        link.dispatchEvent(clickEvent);

        document.body.removeChild(link);
    };

    const handleRedirectToInvoiceDetails = (invoiceNumber: string) => {
        const { history } = props;

        if (invoiceNumber) {
            history.push(`/MyAccount/Invoices/Details?invoiceNumber=${invoiceNumber}`);
        }
    };

    return (
        <>
            {order.properties.invoiceDataNumbers && order.properties.invoiceDataNumbers !== "" && (
                <StyledWrapper {...orderDetailsInvoicesNumbersStyles.wrapper}>
                    <Typography {...styles.titleText} id="orderDetailsInvoicesNumbers">
                        {translate("Invoices #")}:
                    </Typography>
                    <Typography {...styles.contentText} aria-labelledby="orderDetailsInvoicesNumbers">
                        {JSON.parse(order.properties.invoiceDataNumbers)?.map((invoiceNumber: string) => (
                            <StyledWrapper key={invoiceNumber} {...orderDetailsInvoicesNumbersStyles.linksWrapper}>
                                <Link
                                    onClick={() => handleRedirectToInvoiceDetails(invoiceNumber)}
                                    {...orderDetailsInvoicesNumbersStyles.textStyle}
                                >
                                    {invoiceNumber}
                                </Link>
                                <Clickable
                                    onClick={() => handlePDFClick(invoiceNumber)}
                                    data-test-selector="invoiceDetails_PDF"
                                    {...orderDetailsInvoicesNumbersStyles.pdfButton}
                                >
                                    <Icon src={Pdf} {...orderDetailsInvoicesNumbersStyles.iconStyle} />
                                </Clickable>
                            </StyledWrapper>
                        ))}
                    </Typography>
                </StyledWrapper>
            )}
            <Modal
                headline={siteMessage("Custom_InvoicesPDF_Modal_Title", "Invoices PDF")}
                size={200}
                isOpen={isModalOpen}
                handleClose={modalCloseHandler}
            >
                {invoices.map((invoice: any, index) => (
                    <StyledWrapper key={index} {...orderDetailsInvoicesNumbersStyles.modalPDFWrapper}>
                        <Link
                            as="a"
                            href={`data:application/pdf;base64,${invoice.invoicePdf}`}
                            download={`invoice ${selectedInvoiceNumber}_${index + 1}.pdf`}
                            onClick={modalCloseHandler}
                        >
                            {translate(`invoice ${selectedInvoiceNumber}_${index + 1}.pdf`)}
                        </Link>
                    </StyledWrapper>
                ))}
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(withHistory(OrderDetailsInvoicesNumbers)),
    definition: {
        allowedContexts: [OrderDetailsPageContext],
        group: "Order Details",
    },
};

export default widgetModule;
